// src/components/LiveCoverHero.jsx
import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";

function getUploadStatus(eventDateRaw, now) {
  if (!eventDateRaw) return null;

  const s = String(eventDateRaw).trim();
  if (!s) return null;

  let y, m, d;

  const full = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  const short = s.match(/^(\d{1,2})[-/](\d{1,2})$/);

  if (full) {
    y = Number(full[1]);
    m = Number(full[2]) - 1;
    d = Number(full[3]);
  } else if (short) {
    y = now.getFullYear();
    m = Number(short[1]) - 1;
    d = Number(short[2]);
  } else {
    return null;
  }

  const eventDate = new Date(y, m, d);

  if (
    eventDate.getFullYear() !== y ||
    eventDate.getMonth() !== m ||
    eventDate.getDate() !== d
  ) {
    return null;
  }

  const endDay = Date.UTC(y, m, d + 7);
  const today = Date.UTC(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );

  const daysLeft = Math.round(
    (endDay - today) / 86400000
  );

  if (daysLeft <= 0) {
    return { label: "Event ended", ended: true };
  }

  return {
    label: `${daysLeft} ${daysLeft === 1 ? "day" : "days"} left to upload`,
    ended: false,
  };
}

export default function LiveCoverHero({
  coverSrc,
  liveCoverSrc,
  coverZoom = 1,
  hasAnyPhotos,
  details,
  customization = {},
  stickyHeight = 64,
  children,
}) {
  const rawName = details?.eventName || "";
  const rawDate = details?.eventDate || "";
  const rawWelcome = details?.welcomeMessage || "";

  const safeName = rawName.trim() || "Your Event Gallery";
  const safeDate = rawDate.trim() || "";

  const isLiveGuestCover = !coverSrc && !!liveCoverSrc;
  const heroImage = coverSrc || liveCoverSrc || null;

  const fontFamily =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

  const [now, setNow] = useState(() => new Date());
  const [infoOpen, setInfoOpen] = useState(false);

  // Store the actual image dimensions.
  const [imageDimensions, setImageDimensions] = useState(null);

  useEffect(() => {
    setImageDimensions(null);
    if (!heroImage) return;

    let cancelled = false;
    const img = new Image();

    img.onload = () => {
      if (cancelled) return;

      const width = img.naturalWidth;
      const height = img.naturalHeight;

      if (width > 0 && height > 0) {
        setImageDimensions({ width, height });
      }
    };

    img.src = heroImage;

    return () => {
      cancelled = true;
      img.onload = null;
    };
  }, [heroImage]);

  useEffect(() => {
    const nextMidnight = new Date();
    nextMidnight.setHours(24, 0, 0, 0);

    const timer = window.setTimeout(
      () => setNow(new Date()),
      Math.max(
        1000,
        nextMidnight.getTime() - Date.now() + 100
      )
    );

    return () => window.clearTimeout(timer);
  }, [now]);

  useEffect(() => {
    if (!infoOpen) return;

    const timer = window.setTimeout(
      () => setInfoOpen(false),
      5000
    );

    return () => window.clearTimeout(timer);
  }, [infoOpen]);

  const uploadStatus = getUploadStatus(rawDate, now);

  // Match mobile hero proportions to the image.
  // Portrait photos get a taller hero.
  const imageRatio = imageDimensions
    ? imageDimensions.width / imageDimensions.height
    : null;

const mobileRatio = imageRatio
  ? Math.min(imageRatio, 1.25)
  : 0.9;

  const mobileAspectRatio = `${mobileRatio}`;

  return (
    <Box
      sx={{
        width: "min(720px, 95vw)",

        // Mobile follows the photo's shape.
        // Desktop retains the wide hero.
       aspectRatio: {
  xs: "4 / 3",
  sm: "5 / 3",
},

height: {
  xs: 370,
  sm: "auto",
},

minHeight: "auto",

        marginBottom: {
          xs: "-84px",
          sm: "-100px",
        },

        marginTop: {
  xs: "0px",
  sm: "-60px",
},

        borderRadius: "12px",
        overflow: "hidden",
        position: "relative",
        isolation: "isolate",
        backgroundColor: "#171717",

        boxShadow:
          "0 6px 16px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      {/* MAIN PHOTO */}
      {heroImage ? (
        <Box
          component="img"
          src={heroImage}
          alt="Event cover"
          data-priority="true"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",

           objectFit: "cover",
objectPosition: {
  xs: "50% 0%",
  sm: "center 15%",
},

            // Never enlarge live guest photos.
            transform: isLiveGuestCover
              ? "none"
              : `scale(${
                  Number.isFinite(Number(coverZoom))
                    ? Math.max(1, Number(coverZoom))
                    : 1
                })`,

            transformOrigin: "center top",
            display: "block",
            filter: "brightness(0.95)",
           pointerEvents: "auto",
WebkitTouchCallout: "default",
WebkitUserSelect: "auto",
            zIndex: 0,
          }}
        />
      ) : (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            background:
              "radial-gradient(circle at 0% 0%, #0f172a 0%, #020617 40%, #000000 100%)",
          }}
        />
      )}

      {/* SUBTLE GRADIENT */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          pointerEvents: "none",
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.04) 35%, rgba(0,0,0,0.35) 70%, rgba(0,0,0,0.78) 100%)",
        }}
      />

      {/* UPLOAD COUNTDOWN */}
      {uploadStatus && (
        <Box
          sx={{
            position: "absolute",
           top: {
  xs: 20,
  sm: 112,
},
            left: {
              xs: 16,
              sm: 22,
            },
            zIndex: 3,
            maxWidth: "calc(100% - 40px)",
          }}
        >
          <Box
            component="button"
            type="button"
            aria-label={`${uploadStatus.label}. Tap for details.`}
            aria-expanded={infoOpen}
            onClick={() => setInfoOpen((open) => !open)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.7,
              minHeight: 36,
              p: 0,
              border: 0,
              background: "transparent",
              color: "rgba(255,255,255,0.88)",
              fontFamily,
              fontSize: 11,
              fontWeight: 500,
              lineHeight: 1.2,
              textAlign: "left",
              textShadow: "0 1px 5px rgba(0,0,0,0.9)",
              cursor: "pointer",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              style={{ flexShrink: 0 }}
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 11v5" />
              <path d="M12 8h.01" />
            </svg>

            <span>{uploadStatus.label}</span>
          </Box>

          {infoOpen && (
            <Box
              sx={{
                mt: 0.5,
                width: "min(265px, calc(95vw - 44px))",
                p: 1.5,
                borderRadius: "10px",
                backgroundColor: "#fff",
                boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
              }}
            >
              <Typography
                sx={{
                  color: "#1f2937",
                  fontFamily,
                  fontSize: 13,
                  lineHeight: 1.45,
                }}
              >
                {uploadStatus.ended
                  ? "The upload window has ended. Photos and Boomerangs stay in your gallery."
                  : "Guests can upload until 7 days after the event. Photos and Boomerangs stay in your gallery."}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* EVENT TEXT */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          gap: 0.5,
          p: `10px 16px calc(${stickyHeight}px + 18px)`,
          textAlign: "left",
          alignItems: "flex-start",
          pointerEvents: "none",
        }}
      >
        {isLiveGuestCover && (
          <Box
            sx={{
              mb: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: "#e5e7eb",
              backgroundColor: "rgba(15,23,42,0.9)",
              border: "1px solid rgba(148,163,184,0.6)",
            }}
          >
            Live Guest Cover
          </Box>
        )}

        {customization.showTitle !== false && (
          <Typography
            sx={{
              color: "#ffffff",
              fontFamily,
              fontSize: "clamp(24px, 6vw, 48px)",
              fontWeight: customization.titleBold
                ? "700"
                : "600",
              lineHeight: 1.08,
              letterSpacing: "0.2px",
              textShadow: "0 2px 10px rgba(0,0,0,0.7)",
              maxWidth: "92%",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {heroImage
              ? rawName || "Untitled Event"
              : safeName}
          </Typography>
        )}

        {customization.showDate !== false &&
          (heroImage ? rawDate : safeDate) && (
            <Typography
              sx={{
                color: "#ffffff",
                fontFamily,
                fontSize:
                  customization.dateFontSize || "1rem",
                fontWeight: customization.dateBold
                  ? "700"
                  : "500",
                textShadow:
                  "0 2px 8px rgba(0,0,0,0.6)",
                lineHeight: 1.1,
                opacity: 0.95,
              }}
            >
              {heroImage ? rawDate : safeDate}
            </Typography>
          )}

        {customization.showWelcomeMessage !== false && (
          <Typography
            sx={{
              color: "rgba(255,255,255,0.88)",
              fontFamily,
              fontSize: {
                xs: "0.92rem",
                sm: "1rem",
              },
              fontWeight: customization.welcomeBold
                ? "700"
                : "500",
              textShadow:
                "0 2px 8px rgba(0,0,0,0.6)",
              lineHeight: 1.25,
              mt: 0.25,
              px: 0,
              wordBreak: "break-word",
              overflow: "hidden",
              display: {
                xs: "block",
                sm: "-webkit-box",
              },
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: { sm: 2 },
            }}
          >
            {heroImage
              ? rawWelcome || ""
              : ""}
          </Typography>
        )}
      </Box>

      {/* UPLOAD / POV BUTTONS */}
      {children && (
        <Box
          sx={{
            position: "absolute",
            bottom: `calc(-44px + ${stickyHeight}px)`,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 900,
            pointerEvents: "auto",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}
